import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Boxes, BookOpen, LogOut, ChefHat,
  Users, Calendar, Package, PenLine, CircleDollarSign, UserPlus, Crown, Camera, Menu, ShieldCheck, Plug, Waypoints, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { getLocale, getCurrency } from "@/lib/prefs";
import { SettingsTrigger } from "@/components/SettingsDialog";
import Concierge from "@/components/Concierge";
import CorporateChef from "@/components/CorporateChef";
import FoundersCelebration from "@/components/FoundersCelebration";
import WelcomeBackChef from "@/components/WelcomeBackChef";
import FirstPlateWizard from "@/components/FirstPlateWizard";
import ConciergeCarousel from "@/components/ConciergeCarousel";
import ShareModal from "@/components/ShareModal";
import CertifiedBadge from "@/components/CertifiedBadge";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ThemeToggle from "@/components/ThemeToggle";
import api from "@/lib/api";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/ingredients", label: "Ingredients", icon: Boxes, testId: "nav-ingredients" },
  { to: "/recipes", label: "Recipes", icon: BookOpen, testId: "nav-recipes" },
  { to: "/ledger", label: "Ledger", icon: CircleDollarSign, testId: "nav-ledger" },
  { to: "/snapshots", label: "Snapshots", icon: Camera, testId: "nav-snapshots" },
  { to: "/inventory", label: "Inventory", icon: Package, testId: "nav-inventory" },
  { to: "/pos", label: "POS", icon: Plug, testId: "nav-pos" },
  { to: "/sync", label: "Sync Engine", icon: Waypoints, testId: "nav-sync" },
  { to: "/staff", label: "Staff", icon: Users, testId: "nav-staff" },
  { to: "/roster", label: "Roster", icon: Calendar, testId: "nav-roster" },
];

// Admin-only routes: hidden from every non-admin account. Rendered under a
// separate "Admin" section header in both the desktop sidebar and the mobile
// drawer so the supreme admin sees a clear command center on sign-in.
const adminNav = [
  { to: "/admin/system", label: "System", icon: ShieldCheck, testId: "nav-admin-system" },
  { to: "/admin/blog", label: "Blog", icon: PenLine, testId: "nav-blog" },
  { to: "/admin/contributors", label: "Contributors", icon: UserPlus, testId: "nav-contributors" },
  { to: "/admin/media-mentions", label: "Media", icon: BookOpen, testId: "nav-media" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Fetch cert status once. Refreshed on route change so the badge lights up
  // the moment the concierge finalises it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/certification/status");
        if (!cancelled) setCert(data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshCert = async () => {
    try {
      const { data } = await api.get("/certification/status");
      setCert(data);
    } catch {}
  };
  // Cheap poll while the user is on-app; keeps the badge in sync when they
  // finalise via the Concierge popover without a hard reload.
  useEffect(() => {
    const t = setInterval(refreshCert, 15000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex flex-col min-h-screen text-foreground">
      <ImpersonationBanner />
      <ConciergeCarousel />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <aside className="glass-card no-print hidden xl:flex xl:w-72 shrink-0 flex-col rounded-none border-l-0 border-t-0 border-b-0">
        <div className="px-6 py-7 flex items-center gap-4" data-testid="sidebar-brand">
          <img
            src="/brand/mark-256.png"
            alt="Culinaire Specialist"
            className="w-16 h-16 rounded-full object-contain shrink-0"
            style={{
              background: "#0a0806",
              boxShadow:
                "0 0 0 1.5px rgba(212,175,55,0.65), 0 8px 28px -8px rgba(212,175,55,0.65), inset 0 0 0 1px rgba(0,0,0,0.5)",
              imageRendering: "-webkit-optimize-contrast",
            }}
            data-testid="sidebar-logo"
          />
          <div className="min-w-0">
            <div className="brand-mark-primary" data-testid="sidebar-brand-primary">Culinaire</div>
            <div className="brand-mark-secondary" data-testid="sidebar-brand-secondary">Specialist</div>
          </div>
        </div>
        <div className="luxury-divider mx-6" />
        <nav className="flex-1 px-3 py-6 space-y-1">
          {nav.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-[0.95rem] font-medium transition-colors ${
                  isActive
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              <Icon className="w-[1.15rem] h-[1.15rem]" strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}

          {/* Founding Member Share: elevated nav entry so the referral kit
              has a home inside the primary sidebar instead of a floating
              corner pill. Gold gradient border marks it as an incentive. */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("cs:open-share"))}
            data-testid="share-launcher"
            className="w-full flex items-center gap-3 px-3 py-2.5 mt-2 rounded-md text-[0.95rem] font-semibold transition-transform hover:scale-[1.01]"
            style={{
              background:
                "linear-gradient(180deg, rgba(212,175,55,0.22), rgba(212,175,55,0.05))",
              border: "1px solid rgba(212,175,55,0.45)",
              color: "#f5e6b0",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.14), 0 6px 18px -10px rgba(212,175,55,0.55)",
            }}
          >
            <Share2 className="w-[1.15rem] h-[1.15rem]" strokeWidth={1.9} />
            Founding Member Share
          </button>
          {user?.role === "admin" && (
            <>
              <div className="mt-5 mb-2 px-3 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#D4AF37" }} />
                <div className="label-eyebrow" style={{ color: "#D4AF37", fontSize: "0.7rem" }}>Admin</div>
              </div>
              {adminNav.map(({ to, label, icon: Icon, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-[0.95rem] font-medium transition-colors ${
                      isActive
                        ? "bg-secondary text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`
                  }
                >
                  <Icon className="w-[1.15rem] h-[1.15rem]" strokeWidth={1.75} />
                  {label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="mb-3 text-xs text-muted-foreground">
            <div className="font-medium text-foreground truncate flex items-center gap-1.5" data-testid="current-user-name">
              {user?.name || user?.email}
              {user?.subscription_tier === "pro" && (
                <Crown className="w-3 h-3 shrink-0" style={{ color: "#f2dfb5" }} aria-label="Pro" />
              )}
              {user?.role === "admin" && (
                <span
                  data-testid="admin-badge"
                  className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.6rem] uppercase font-semibold tracking-[0.18em]"
                  style={{
                    background: "linear-gradient(180deg, rgba(212,175,55,0.28), rgba(212,175,55,0.06))",
                    border: "1px solid rgba(212,175,55,0.55)",
                    color: "#f5e6b0",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14)",
                  }}
                >
                  <ShieldCheck className="w-2.5 h-2.5" />
                  Supreme Admin
                </span>
              )}
            </div>
            <div className="truncate">{user?.email}</div>
            {cert?.certified && (
              <div className="mt-2">
                <CertifiedBadge size="sm" certificateNumber={cert.certificate_number} />
              </div>
            )}
            <div className="mt-2 label-eyebrow text-[0.55rem]">
              {getLocale()} • {getCurrency()}
            </div>
          </div>
          {user?.subscription_tier !== "pro" && user?.role !== "admin" && (
            <NavLink
              to="/billing"
              data-testid="upgrade-nudge"
              className="block mb-2 rounded-lg px-3 py-2.5 text-xs font-medium transition-all"
              style={{
                background: "linear-gradient(180deg, rgba(232,207,154,0.14), rgba(232,207,154,0.04))",
                border: "1px solid rgba(232,207,154,0.30)",
                color: "#f2dfb5",
              }}
            >
              <div className="flex items-center gap-1.5">
                <Crown className="w-3.5 h-3.5" />
                Upgrade to Pro
              </div>
              <div className="text-[0.65rem] mt-0.5 opacity-70 font-normal">
                Turn on AI Invoice & Plating Coach
              </div>
            </NavLink>
          )}
          {(user?.subscription_tier === "pro" || user?.role === "admin") && (
            <NavLink
              to="/billing"
              data-testid="billing-link"
              className="block mb-2 rounded px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
            >
              <Crown className="w-3.5 h-3.5" style={{ color: "#f2dfb5" }} />
              {user?.role === "admin" ? "Billing" : "Manage billing"}
            </NavLink>
          )}
          <SettingsTrigger />
          <div className="mt-2 mb-1">
            <ThemeToggle />
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 mt-1"
            onClick={doLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile + tablet header. Visible up to xl (1280px) so iPad
          portrait, iPad landscape (under 1280), and every phone gets a
          centered glass command bar with the drawer trigger on the left,
          a brand mark centered, and a Share pill on the right. */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="glass-card xl:hidden grid grid-cols-[auto_1fr_auto] items-center gap-2 px-4 py-3 sticky top-0 z-30 no-print rounded-none" data-testid="universal-tablet-header">
          <div className="flex items-center gap-2 justify-self-start">
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              {/* Direct onClick trigger. We deliberately do NOT use
                  SheetTrigger asChild here: on mobile Chrome the
                  combination of Radix's pointerdown/pointerup event
                  chain and our tactile CSS transform on :active can
                  cause Radix to drop the toggle (pointer target moves
                  1px during press, Radix treats it as ambiguous and
                  skips the state flip). A direct onClick is
                  bulletproof and needs no event forwarding. */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="mobile-nav-open"
                aria-label="Open navigation menu"
                aria-expanded={mobileNavOpen}
                aria-controls="mobile-nav-drawer-content"
                className="px-2"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <SheetContent
                id="mobile-nav-drawer-content"
                side="left"
                className="w-[84%] max-w-[320px] p-0 flex flex-col"
                data-testid="mobile-nav-drawer"
              >
                <SheetTitle className="sr-only">Culinaire Specialist navigation</SheetTitle>
                <div className="px-6 py-7 flex items-center gap-4 border-b border-border">
                  <img
                    src="/brand/mark-256.png"
                    alt="Culinaire Specialist"
                    className="w-14 h-14 rounded-full object-contain shrink-0"
                    style={{
                      background: "#0a0806",
                      boxShadow:
                        "0 0 0 1.5px rgba(212,175,55,0.65), 0 8px 26px -8px rgba(212,175,55,0.6), inset 0 0 0 1px rgba(0,0,0,0.5)",
                    }}
                  />
                  <div className="min-w-0">
                    <div className="brand-mark-primary" style={{ fontSize: "1.22rem" }}>Culinaire</div>
                    <div className="brand-mark-secondary">Specialist</div>
                  </div>
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                  {nav.map(({ to, label, icon: Icon, testId }) => (
                    <SheetClose asChild key={to}>
                      <NavLink
                        to={to}
                        data-testid={`${testId}-mobile`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-3 rounded-md text-[0.95rem] font-medium transition-colors ${
                            isActive
                              ? "bg-secondary text-foreground"
                              : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                          }`
                        }
                      >
                        <Icon className="w-[1.15rem] h-[1.15rem]" strokeWidth={1.75} />
                        {label}
                      </NavLink>
                    </SheetClose>
                  ))}

                  {/* Founding Member Share inside the mobile drawer so the
                      floating pill never overlaps the dashboard. */}
                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new Event("cs:open-share"))}
                      data-testid="share-launcher-mobile"
                      className="w-full flex items-center gap-3 px-3 py-3 mt-2 rounded-md text-[0.95rem] font-semibold transition-transform active:scale-[0.99]"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(212,175,55,0.22), rgba(212,175,55,0.05))",
                        border: "1px solid rgba(212,175,55,0.45)",
                        color: "#f5e6b0",
                        boxShadow:
                          "inset 0 1px 0 rgba(255,255,255,0.14), 0 6px 18px -10px rgba(212,175,55,0.55)",
                      }}
                    >
                      <Share2 className="w-[1.15rem] h-[1.15rem]" strokeWidth={1.9} />
                      Founding Member Share
                    </button>
                  </SheetClose>
                  {user?.role === "admin" && (
                    <>
                      <div className="mt-5 mb-2 px-3 flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#D4AF37" }} />
                        <div className="label-eyebrow" style={{ color: "#D4AF37", fontSize: "0.7rem" }}>Admin</div>
                      </div>
                      {adminNav.map(({ to, label, icon: Icon, testId }) => (
                        <SheetClose asChild key={to}>
                          <NavLink
                            to={to}
                            data-testid={`${testId}-mobile`}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-3 py-3 rounded-md text-[0.95rem] font-medium transition-colors ${
                                isActive
                                  ? "bg-secondary text-foreground"
                                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                              }`
                            }
                          >
                            <Icon className="w-[1.15rem] h-[1.15rem]" strokeWidth={1.75} />
                            {label}
                          </NavLink>
                        </SheetClose>
                      ))}
                    </>
                  )}
                </nav>
                <div className="p-4 border-t border-border">
                  <div className="mb-3 text-xs text-muted-foreground">
                    <div className="font-medium text-foreground truncate flex items-center gap-1.5">
                      {user?.name || user?.email}
                      {user?.subscription_tier === "pro" && (
                        <Crown className="w-3 h-3 shrink-0" style={{ color: "#f2dfb5" }} aria-label="Pro" />
                      )}
                      {user?.role === "admin" && (
                        <span
                          data-testid="admin-badge-mobile"
                          className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.6rem] uppercase font-semibold tracking-[0.18em]"
                          style={{
                            background: "linear-gradient(180deg, rgba(212,175,55,0.28), rgba(212,175,55,0.06))",
                            border: "1px solid rgba(212,175,55,0.55)",
                            color: "#f5e6b0",
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14)",
                          }}
                        >
                          <ShieldCheck className="w-2.5 h-2.5" />
                          Supreme Admin
                        </span>
                      )}
                    </div>
                    <div className="truncate">{user?.email}</div>
                    {cert?.certified && (
                      <div className="mt-2">
                        <CertifiedBadge size="sm" certificateNumber={cert.certificate_number} />
                      </div>
                    )}
                    <div className="mt-2 label-eyebrow text-[0.55rem]">
                      {getLocale()} • {getCurrency()}
                    </div>
                  </div>
                  {user?.subscription_tier !== "pro" && user?.role !== "admin" && (
                    <SheetClose asChild>
                      <NavLink
                        to="/billing"
                        data-testid="upgrade-nudge-mobile"
                        className="block mb-2 rounded-lg px-3 py-2.5 text-xs font-medium transition-all"
                        style={{
                          background: "linear-gradient(180deg, rgba(232,207,154,0.14), rgba(232,207,154,0.04))",
                          border: "1px solid rgba(232,207,154,0.30)",
                          color: "#f2dfb5",
                        }}
                      >
                        <div className="flex items-center gap-1.5">
                          <Crown className="w-3.5 h-3.5" />
                          Upgrade to Pro
                        </div>
                        <div className="text-[0.65rem] mt-0.5 opacity-70 font-normal">
                          Turn on AI Invoice and Plating Coach
                        </div>
                      </NavLink>
                    </SheetClose>
                  )}
                  {(user?.subscription_tier === "pro" || user?.role === "admin") && (
                    <SheetClose asChild>
                      <NavLink
                        to="/billing"
                        data-testid="billing-link-mobile"
                        className="mb-2 rounded px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                      >
                        <Crown className="w-3.5 h-3.5" style={{ color: "#f2dfb5" }} />
                        {user?.role === "admin" ? "Billing" : "Manage billing"}
                      </NavLink>
                    </SheetClose>
                  )}
                  <SettingsTrigger />
                  <div className="mt-2 mb-1">
                    <ThemeToggle />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 mt-1"
                    onClick={() => { setMobileNavOpen(false); doLogout(); }}
                    data-testid="logout-btn-mobile-drawer"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Center: brand mark. Perfectly centered on every tablet width. */}
          <div className="flex items-center justify-center gap-2.5 justify-self-center">
            <img
              src="/brand/mark-256.png"
              alt="Culinaire Specialist"
              className="w-9 h-9 rounded-full object-contain shrink-0"
              style={{
                background: "#0a0806",
                boxShadow: "0 0 0 1px rgba(212,175,55,0.55), 0 4px 12px -4px rgba(212,175,55,0.45)",
              }}
            />
            <span
              className="brand-mark-primary hidden sm:inline"
              style={{ fontSize: "0.98rem", letterSpacing: "0.12em" }}
              data-testid="mobile-brand"
            >
              Culinaire
            </span>
          </div>

          {/* Right: quick actions. Share pill + theme toggle + logout.
              Admin God-View link surfaces here as a gold-rim shield so
              the supreme admin never has to open the drawer to jump into
              the admin cockpit. */}
          <div className="flex items-center gap-1.5 justify-self-end">
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="admin-godview-tablet"
                className="flex items-center gap-1.5 px-2.5 h-11 rounded-full transition-colors"
                title="Admin God View"
                style={{
                  background: "linear-gradient(180deg, rgba(212,175,55,0.20), rgba(212,175,55,0.04))",
                  border: "1px solid rgba(212,175,55,0.45)",
                  color: "#f5e6b0",
                }}
              >
                <ShieldCheck className="w-4 h-4" strokeWidth={2} />
                <span className="hidden sm:inline text-[0.72rem] tracking-[0.16em] uppercase font-semibold">Admin</span>
              </NavLink>
            )}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("cs:open-share"))}
              data-testid="share-launcher-tablet"
              className="flex items-center gap-1.5 px-2.5 h-11 rounded-full transition-transform active:scale-[0.98]"
              title="Founding Member Share"
              style={{
                background: "linear-gradient(180deg, rgba(232,207,154,0.22), rgba(232,207,154,0.05))",
                border: "1px solid rgba(232,207,154,0.45)",
                color: "#f5e6b0",
              }}
            >
              <Share2 className="w-4 h-4" strokeWidth={1.9} />
              <span className="hidden sm:inline text-[0.72rem] tracking-[0.14em] uppercase font-semibold">Share</span>
            </button>
            <ThemeToggle compact />
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-btn-mobile" aria-label="Sign out">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <main className="flex-1 min-w-0 pb-0">
          <Outlet />
        </main>
      </div>
      </div>
      <Concierge />
      <CorporateChef />
      <FirstPlateWizard />
      <ShareModal />
      <FoundersCelebration />
      <WelcomeBackChef />
    </div>
  );
}
